import { BoxProps } from "@mantine/core";
import { useAtomValue } from "jotai";
import { CSSProperties, ElementType, ReactNode } from "react";

import BottomDrawer from "components/BottomDrawer";
import ContentContainer from "components/ContentContainer";
import Flex from "components/Flex";
import NavBar from "components/NavBar";
import ProgressSteps from "components/ProgressSteps";
import StickyButtonsBox from "components/StickyButtonsBox";
import Text from "components/Text";

import { flowWithProgressStepperAtom } from "atoms/appFlow";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";

export type PageLayoutProps<Element> = BoxProps<Element> & {
  children: ReactNode;
  bottomButtons?: ReactNode;
  as?: Element;
  currentStep?: number;
  title?: ReactNode;
  showNavbar?: boolean;
  contentSpacing?: CSSProperties["justifyContent"];
  forceContinue?: boolean;
  required?: boolean;
  dataTestId?: string;
};

export const PageLayout = <Element extends ElementType = "div">({
  children,
  bottomButtons,
  currentStep,
  as,
  title,
  showNavbar = false,
  contentSpacing,
  forceContinue = false,
  required,
  dataTestId = "",
  ...boxProps
}: PageLayoutProps<Element>) => {
  const steps = useAtomValue(flowWithProgressStepperAtom);
  const { onNext } = usePageRouting();
  const { t } = useClientTranslation();

  return (
    <>
      <ContentContainer minHeight={42}>
        {!!currentStep && <ProgressSteps steps={steps.length} currentStep={currentStep} />}
        {showNavbar && <NavBar />}
      </ContentContainer>

      <Flex flex="1" component={as} justify="space-between" {...boxProps}>
        <ContentContainer flex="1" contentSpacing={contentSpacing}>
          {title && (
            <Flex px={24} mb={8}>
              <Text dataTestId={dataTestId} as="h1" sizeVariant="xl" weight={600}>
                {title}

                {required === false && (
                  <Text sizeVariant="xl" as="span" weight={400} style={{ display: "block" }}>
                    ({t("additional_documents.header.optional")})
                  </Text>
                )}
              </Text>
            </Flex>
          )}
          {children}
        </ContentContainer>

        {bottomButtons && <StickyButtonsBox shadow={true}>{bottomButtons}</StickyButtonsBox>}

        <BottomDrawer
          isOpen={forceContinue}
          callToActionOnClick={onNext}
          closeOnClickOutside={false}
          heading={t("read_only.header")}
          callToActionText={"Continue"}
        >
          <Text sizeVariant="md">{t("read_only.instruction")}</Text>
        </BottomDrawer>
      </Flex>
    </>
  );
};
